import { Injectable } from '@angular/core';
import { Store } from '../../services/store';
import { AddNodeDropdownProvider } from './add-node-dropdown.provider';
import { Point } from '../../models/point';
import { Observable, of, tap } from 'rxjs';
import { toVoid } from '../../utils/void';
import { BomItem } from '../../models/bom-item.model';
import { isExactBomItemMatch } from '../../utils/exact-match';

const MIN_SEARCH_LENGTH = 3;

export interface AddNodeDropdownState {
  loading: boolean;
  openAt: Point | undefined; // rename to position ???
  search: string | undefined;
  // the term searchResult answers — Enter never acts on a list that is not the term's (0110)
  searchedTerm: string | undefined;
  searchResult: BomItem[] | undefined;
  // index into searchResult, or searchResult.length for the create row; undefined until ↑/↓
  highlighted: number | undefined;
}

/**
 * What Enter takes: an item of the list, the create row, or nothing.
 */
export type AddNodeEnterTarget = { item: BomItem } | 'create' | undefined;

const INITIAL_STATE: AddNodeDropdownState = {
  loading: false,
  openAt: undefined,
  search: undefined,
  searchedTerm: undefined,
  searchResult: undefined,
  highlighted: undefined
};

// Same rule as DropdownComponent (0110), kept apart: replacing this legacy dropdown is its own refactor

function isSettled(state: AddNodeDropdownState): boolean {
  return state.searchedTerm === state.search;
}

function exactMatchIndex(state: AddNodeDropdownState): number {
  const term = state.search ?? '';

  if (!term.trim()) {
    return -1;
  }

  return (state.searchResult ?? []).findIndex(item => isExactBomItemMatch(item, term));
}

/**
 * `+ Utwórz „…”` ends an answered list with no item named (or numbered) as typed.
 */
export function showsCreateRow(state: AddNodeDropdownState): boolean {
  const term = state.search ?? '';

  return !!term.trim()
    && term.trim().length >= MIN_SEARCH_LENGTH
    && isSettled(state)
    && exactMatchIndex(state) === -1;
}

function rowCount(state: AddNodeDropdownState): number {
  return (state.searchResult?.length ?? 0) + (showsCreateRow(state) ? 1 : 0);
}

/**
 * Enter takes the highlighted row; with none, the exactly matching item, else the
 * create row. Nothing while the list is still on its way.
 */
export function enterTarget(state: AddNodeDropdownState): AddNodeEnterTarget {
  if (!isSettled(state)) {
    return undefined;
  }

  const items = state.searchResult ?? [];
  const index = state.highlighted ?? exactMatchIndex(state);

  if (index >= 0 && index < items.length) {
    return { item: items[index] };
  }

  return showsCreateRow(state) ? 'create' : undefined;
}

@Injectable({ providedIn: 'root' })
export class AddNodeDropdownStore extends Store<AddNodeDropdownState> {
  constructor(private provider: AddNodeDropdownProvider) {
    super(INITIAL_STATE);
  }

  init(): void {
  }

  open(x: number, y: number): void {
    this.update(state => ({ ...state, openAt: { x, y } }));
  }

  openAt(position: Point | undefined): void {
    this.update(state => ({ ...state, openAt: position }));
  }

  close(): void {
    this.update(state => ({ ...state, openAt: undefined }));
  }

  // ↓ stops at the last row, the create row included
  highlightNext(): void {
    this.update(state => {
      const count = rowCount(state);

      if (count === 0) {
        return state;
      }

      const highlighted = state.highlighted === undefined ? 0 : Math.min(state.highlighted + 1, count - 1);

      return { ...state, highlighted };
    });
  }

  // ↑ past the first row hands the choice back to the typed text
  highlightPrevious(): void {
    this.update(state => {
      if (state.highlighted === undefined) {
        return state;
      }

      return { ...state, highlighted: state.highlighted > 0 ? state.highlighted - 1 : undefined };
    });
  }

  // the term follows the input at once, so the create row names the full text;
  // typing hands the choice back to the text
  setSearch(search: string): void {
    this.update(state => ({ ...state, search, highlighted: undefined }));
  }

  // the debounced half of a search — the component decides when, the store only fetches
  fetch(search: string): Observable<void> {
    if (search.trim().length < MIN_SEARCH_LENGTH) {
      // too short to search: no list, and nothing stale from a longer term under it
      this.update(state => ({ ...state, searchedTerm: search, searchResult: undefined, highlighted: undefined }));
      return of(undefined);
    }

    this.update(state => ({ ...state, loading: true }));

    // searched trimmed, as Enter compares: "PRZYCZEPA " must find "Przyczepa" to match it
    return this.provider.searchBomItems(search.trim())
      .pipe(
        tap(items => {
          this.update(state => ({
            ...state,
            loading: false,
            searchedTerm: search,
            highlighted: undefined,
            searchResult: items
          }))
        }),
        toVoid()
      );
  }
}
